import { refreshSession } from '../lib/api/index.js';
import { getAccessToken, subscribeToAccessToken } from '../lib/tokenStorage.js';

/**
 * Keeps the access token fresh while the app is open.
 *
 * How long someone stays signed in is decided by the SERVER: their session
 * lasts until the administrator's maximum lifetime (or idle timeout) and is
 * checked on every request. Access tokens are short-lived copies of it, and
 * this keeper swaps each for a new one when about three-quarters of its life
 * has gone.
 *
 * What it deliberately does NOT do is sign anyone out by reading the clock.
 * The old keeper ended the session when the token's `exp` passed on THIS
 * machine's clock - so a laptop asleep past the expiry, a background tab whose
 * timers were throttled, or a PC clock an hour off signed people out of a
 * session that was perfectly alive. Now the only thing that signs out is the
 * server saying the session is over (a 401 from `/auth/refresh`, or a socket
 * `system.sessionRevoked`); a missed timer just means the next request
 * refreshes first.
 *
 * Timing uses intervals measured on this machine (token lifetime from the
 * token's own `iat`/`exp`, both server times), never absolute comparisons
 * between the two clocks.
 */

/** Refresh when this fraction of the token's life has passed. */
const REFRESH_AT = 0.75;
/** Never refresh more often than this, whatever the token says. */
const MIN_INTERVAL_MS = 30_000;
/** Lifetime assumed for a token whose claims cannot be read. */
const FALLBACK_LIFETIME_MS = 15 * 60 * 1000;
/** After a failed attempt (offline, server restarting), try again this soon. */
const RETRY_MS = 30_000;
/** `setTimeout` overflows past this and fires at once. */
const MAX_TIMER_MS = 2 ** 31 - 1;

/** The token's lifetime from its own claims, both stamped by the server. */
function lifetimeOf(token: string | null): number {
  const payload = token?.split('.')[1];

  if (!payload) {
    return FALLBACK_LIFETIME_MS;
  }

  try {
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const claims = JSON.parse(json) as { exp?: unknown; iat?: unknown };

    return typeof claims.exp === 'number' &&
      typeof claims.iat === 'number' &&
      claims.exp > claims.iat
      ? (claims.exp - claims.iat) * 1000
      : FALLBACK_LIFETIME_MS;
  } catch {
    return FALLBACK_LIFETIME_MS;
  }
}

/** Starts keeping the current session. Returns the stop function. */
export function startSessionKeeper(): () => void {
  let timer: ReturnType<typeof setTimeout> | undefined;
  /** When the current token arrived, on this machine's clock. */
  let receivedAt = Date.now();
  let lastAttemptAt = 0;

  function schedule(delay: number): void {
    clearTimeout(timer);
    timer = setTimeout(() => void refresh(), Math.min(Math.max(delay, 0), MAX_TIMER_MS));
  }

  function dueIn(): number {
    return Math.max(lifetimeOf(getAccessToken()) * REFRESH_AT, MIN_INTERVAL_MS);
  }

  async function refresh(): Promise<void> {
    if (!getAccessToken()) {
      return;
    }

    lastAttemptAt = Date.now();
    const outcome = await refreshSession();

    if (outcome.status === 'failed') {
      schedule(RETRY_MS);
    }
    // 'ok' reschedules through the token subscription below; 'ended' has
    // already signed out with the server's reason.
  }

  /** On waking up or coming back to the tab: refresh if the timer may have been missed. */
  function onResume(): void {
    if (document.visibilityState !== 'visible') {
      return;
    }

    const elapsed = Date.now() - receivedAt;

    if (elapsed >= dueIn() && Date.now() - lastAttemptAt >= MIN_INTERVAL_MS) {
      void refresh();
    }
  }

  const stopWatchingToken = subscribeToAccessToken((token) => {
    if (token) {
      receivedAt = Date.now();
      schedule(dueIn());
    } else {
      clearTimeout(timer);
    }
  });

  document.addEventListener('visibilitychange', onResume);
  window.addEventListener('focus', onResume);
  window.addEventListener('online', onResume);

  // The stored token may be hours old (the app was closed): start fresh.
  void refresh();

  return () => {
    stopWatchingToken();
    clearTimeout(timer);
    document.removeEventListener('visibilitychange', onResume);
    window.removeEventListener('focus', onResume);
    window.removeEventListener('online', onResume);
  };
}
